import React, { useState } from "react";
import { FiCreditCard, FiCheckCircle, FiClock, FiShield } from "react-icons/fi";
import { LuSparkles } from "react-icons/lu";

const SubscriptionTab = () => {
  // --------------------------
  // PLAN CONFIG
  // --------------------------
  const plans = {
    Starter: {
      name: "Starter",
      price: "₹0",
      listingLimit: 1,
      visibilityDays: 15,
      color: "#0CC763",
    },
    "Pro Agent": {
      name: "Pro Agent",
      price: "₹799",
      listingLimit: 15,
      visibilityDays: 45,
      color: "#735DFF",
    },
    "Elite Realty": {
      name: "Elite Realty",
      price: "₹1499",
      listingLimit: "Unlimited",
      visibilityDays: 90,
      color: "#FF5A29",
    },
  };

  // --------------------------
  // DEFAULT USER DATA
  // Starter is selected by default
  // --------------------------
  const [userData, setUserData] = useState({
    plan: "Starter",
    planExpiry: new Date(
      new Date().setDate(new Date().getDate() + plans["Starter"].visibilityDays)
    ),
    propertiesListed: 0,
  });

  // --------------------------
  // STAT CARDS CONFIG
  // --------------------------
  const subscriptionStats = [
    {
      id: 1,
      title: "Active Plan",
      value: userData.plan,
      icon: FiCheckCircle,
      iconBg: plans[userData.plan].color,
    },
    {
      id: 2,
      title: "Properties Listed",
      value: `${userData.propertiesListed}/${
        plans[userData.plan].listingLimit
      }`,
      icon: FiCreditCard,
      iconBg: "#735DFF",
    },
    {
      id: 3,
      title: "Days Remaining",
      value:
        Math.ceil(
          (new Date(userData.planExpiry) - new Date()) / (1000 * 60 * 60 * 24)
        ) + " Days",
      icon: FiClock,
      iconBg: "#FF5A29",
    },
    {
      id: 4,
      title: "Renewal Date",
      value: userData.planExpiry.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
      icon: FiShield,
      iconBg: "#0C9CFC",
    },
  ];

  // --------------------------
  // HANDLE PLAN CHANGE
  // --------------------------
  const handlePlanSelect = (planName) => {
    const plan = plans[planName];
    setUserData({
      ...userData,
      plan: planName,
      planExpiry: new Date(
        new Date().setDate(new Date().getDate() + plan.visibilityDays)
      ),
    });
  };

  return (
    <div className="space-y-6">
      {/* -----------------------------
        TOP STAT CARDS
      -------------------------------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {subscriptionStats.map((stat) => (
          <div
            key={stat.id}
            className="bg-white rounded-lg p-4 sm:p-5 shadow-sm"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-gray-900 font-medium text-sm mb-2">
                  {stat.title}
                </p>
                <h3 className="text-2xl font-semibold text-gray-900">
                  {stat.value}
                </h3>
              </div>
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{ backgroundColor: stat.iconBg }}
              >
                <stat.icon className="text-white text-xl w-7 h-7" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* -----------------------------
        ACTIVE PLAN BANNER
      -------------------------------- */}
      {/* <div className="bg-primary hover:bg-primary/90 rounded-lg p-6 text-white">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h3 className="text-2xl font-bold mb-2">
              {userData.plan} Plan Active
            </h3>
            <p className="text-white/90">
              Your plan renews on{" "}
              {new Date(userData.planExpiry).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </p>
          </div>
          <button className="bg-white text-primary px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap">
            Manage Subscription
          </button>
        </div>
      </div> */}

      {/* -----------------------------
        AVAILABLE PLANS (CARDS)
      -------------------------------- */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 tracking-tight">
          Available Plans
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* STARTER PLAN */}
          <div
            className={`relative rounded-2xl border shadow-sm bg-white p-6 transition-all ${
              userData.plan === "Starter"
                ? "ring-2 ring-primary"
                : "hover:shadow-lg"
            }`}
          >
            <h3 className="text-xl font-bold text-gray-900">Starter Plan</h3>
            <p className="text-gray-500 text-sm mb-4">(Free)</p>

            <p className="text-3xl font-bold text-gray-900 mb-4">₹0</p>

            <ul className="space-y-2 text-gray-700 mb-6">
              <li>• 1 Property Listing</li>
              <li>• Visibility: 15 Days</li>
              <li>• Basic Exposure</li>
              <li>• No Boosting</li>
            </ul>

            <button
              onClick={() => handlePlanSelect("Starter")}
              className={`w-full py-3 rounded-lg font-semibold transition ${
                userData.plan === "Starter"
                  ? "bg-primary text-white"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700"
              }`}
            >
              {userData.plan === "Starter" ? "Selected" : "Choose Starter"}
            </button>
          </div>

          {/* PRO AGENT PLAN */}
          <div
            className={`relative rounded-2xl border shadow-sm bg-gradient-to-br from-blue-50 to-purple-50 p-6 transition-all ${
              userData.plan === "Pro Agent"
                ? "ring-2 ring-blue-600"
                : "hover:shadow-lg"
            }`}
          >
            <h3 className="text-xl font-bold text-gray-900">Pro Agent</h3>
            <p className="text-gray-600 text-sm mb-4">Most Popular</p>

            <p className="text-3xl font-bold text-gray-900 mb-4">₹799</p>

            <ul className="space-y-2 text-gray-700 mb-6">
              <li>• 15 Property Listings</li>
              <li>• Visibility: 45 Days</li>
              <li>• Priority Placement</li>
              <li>• Agent Analytics</li>
            </ul>

            <button
              onClick={() => handlePlanSelect("Pro Agent")}
              className={`w-full py-3 rounded-lg font-semibold transition ${
                userData.plan === "Pro Agent"
                  ? "bg-blue-600 text-white"
                  : "bg-white hover:bg-gray-100 text-blue-700 border border-blue-600"
              }`}
            >
              {userData.plan === "Pro Agent" ? "Selected" : "Choose Pro Agent"}
            </button>
          </div>

          {/* ELITE REALTY PLAN */}
          <div
            className={`relative rounded-2xl border shadow-sm bg-gradient-to-br from-pink-50 to-purple-50 p-6 transition-all ${
              userData.plan === "Elite Realty"
                ? "ring-2 ring-pink-600"
                : "hover:shadow-lg"
            }`}
          >
            <h3 className="text-xl font-bold text-gray-900">Elite Realty</h3>
            <p className="text-gray-600 text-sm mb-4">Premium Experience</p>

            <p className="text-3xl font-bold text-gray-900 mb-4">₹1499</p>

            <ul className="space-y-2 text-gray-700 mb-6">
              <li>• Unlimited Listings</li>
              <li>• Visibility: 90 Days</li>
              <li>• Top Tier Placement</li>
              <li>• Lead Booster Access</li>
            </ul>

            <button
              onClick={() => handlePlanSelect("Elite Realty")}
              className={`w-full py-3 rounded-lg font-semibold transition ${
                userData.plan === "Elite Realty"
                  ? "bg-pink-600 text-white"
                  : "bg-white hover:bg-gray-100 text-pink-600 border border-pink-600"
              }`}
            >
              {userData.plan === "Elite Realty" ? "Selected" : "Choose Elite"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionTab;

// import React, { useState } from "react";
// import { FiCreditCard, FiCheckCircle, FiClock, FiShield } from "react-icons/fi";
// import { LuSparkles } from "react-icons/lu";

// const SubscriptionTab = () => {
//   const [userData, setUserData] = useState({
//     fullName: "Mahi Kumar",
//     email: "mahi.kumar@example.com",
//     phone: "+91 98765 43210",
//     preferredCity: "Bhubaneswar",
//     role: "builder-agent",
//     profileImage: null,
//     plan: "Free",
//     planExpiry: new Date("2025-12-01"),
//     companyName: "Mahi Properties",
//     companyWebsite: "www.mahiproperties.com",
//     reraNumber: "ORA/0145/2022",
//     operatingSince: "2010",
//     propertiesForSale: "30",
//     dealsClosed: "20",
//     teamMembers: "10",
//     preferredLocalities: [
//       "Patia",
//       "Chandrasekharpur",
//       "Nayapalli",
//       "Khurdha",
//       "Cuttack",
//     ],
//   });
//   const subscriptionStats = [
//     {
//       id: 1,
//       title: "Active Plan",
//       value: userData.plan || "Free",
//       icon: FiCheckCircle,
//       iconBg: "#0CC763",
//     },
//     {
//       id: 2,
//       title: "Properties Listed",
//       value: "12/25",
//       icon: FiCreditCard,
//       iconBg: "#735DFF",
//     },
//     {
//       id: 3,
//       title: "Days Remaining",
//       value: userData.planExpiry
//         ? Math.ceil(
//             (new Date(userData.planExpiry) - new Date()) / (1000 * 60 * 60 * 24)
//           ) + " Days"
//         : "N/A",
//       icon: FiClock,
//       iconBg: "#FF5A29",
//     },
//     {
//       id: 4,
//       title: "Renewal Date",
//       value: userData.planExpiry
//         ? new Date(userData.planExpiry).toLocaleDateString("en-US", {
//             month: "short",
//             day: "numeric",
//             year: "numeric",
//           })
//         : "N/A",
//       icon: FiShield,
//       iconBg: "#0C9CFC",
//     },
//   ];

//   return (
//     <div className="space-y-6">
//       {/* Subscription Stats */}
//       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
//         {subscriptionStats.map((stat) => (
//           <div
//             key={stat.id}
//             className="bg-white rounded-lg p-4 sm:p-5 shadow-sm"
//           >
//             <div className="flex justify-between items-start mb-4">
//               <div>
//                 <p className="text-gray-900 font-medium text-sm mb-2">
//                   {stat.title}
//                 </p>
//                 <h3 className="text-2xl font-semibold text-gray-900">
//                   {stat.value}
//                 </h3>
//               </div>
//               <div
//                 className="w-12 h-12 rounded-full flex items-center justify-center"
//                 style={{ backgroundColor: stat.iconBg }}
//               >
//                 <stat.icon className="text-white text-xl w-7 h-7" />
//               </div>
//             </div>
//           </div>
//         ))}
//       </div>

//       {/* Current Plan Banner */}
//       <div className="bg-primary hover:bg-primary/90 rounded-lg p-6 text-white">
//         <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
//           <div>
//             <h3 className="text-2xl font-bold mb-2">
//               {userData.plan} Plan Active
//             </h3>
//             <p className="text-white/90">
//               Your plan renews on{" "}
//               {new Date(userData.planExpiry).toLocaleDateString("en-US", {
//                 month: "long",
//                 day: "numeric",
//                 year: "numeric",
//               })}
//             </p>
//           </div>
//           <button className="bg-white text-primary px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap">
//             Manage Subscription
//           </button>
//         </div>
//       </div>

//       {/* Coming Soon */}
//       <div>
//         <h2 className="text-xl font-bold text-gray-900 mb-4 tracking-tight">
//           Available Plans
//         </h2>

//         <div className="relative overflow-hidden bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 rounded-2xl shadow-xl border border-purple-100 backdrop-blur-md">
//           {/* Soft Glow Background */}
//           <div className="absolute top-0 right-0 w-72 h-72 bg-gradient-to-br from-purple-300/20 to-blue-300/20 rounded-full blur-3xl animate-pulse-slow"></div>
//           <div
//             className="absolute bottom-0 left-0 w-72 h-72 bg-gradient-to-tr from-pink-300/20 to-purple-300/20 rounded-full blur-3xl animate-pulse-slow"
//             style={{ animationDelay: "0.6s" }}
//           ></div>

//           <div className="relative z-10 p-12 text-center">
//             {/* Icon Section */}
//             <div className="flex justify-center mb-6">
//               <div className="relative">
//                 <div className="w-24 h-24 bg-gradient-to-br from-purple-500 via-blue-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg ring-4 ring-white/40 animate-float">
//                   <LuSparkles className="w-12 h-12 text-white drop-shadow-md" />
//                 </div>
//                 <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center shadow-md animate-bounce-slow">
//                   <span className="text-white font-bold text-xs">✨</span>
//                 </div>
//               </div>
//             </div>

//             {/* Heading */}
//             <h3 className="text-4xl font-extrabold bg-gradient-to-r from-purple-600 via-blue-600 to-pink-600 bg-clip-text text-transparent mb-4 leading-snug drop-shadow-sm">
//               Exciting Plans Coming Soon!
//             </h3>

//             {/* Description */}
//             <p className="text-lg text-gray-700 mb-6 max-w-2xl mx-auto leading-relaxed">
//               We're crafting amazing subscription plans tailored just for you.
//               Get ready for flexible pricing, premium features, and exclusive
//               benefits that will transform your property management experience.
//             </p>

//             {/* Feature Pills */}
//             <div className="flex flex-wrap justify-center gap-4 mb-8">
//               <div className="flex items-center gap-2 bg-white/80 backdrop-blur-xl px-4 py-2 rounded-full shadow-sm hover:shadow-md transition-all duration-200">
//                 <FiCheckCircle className="text-green-500 w-5 h-5" />
//                 <span className="text-sm font-medium text-gray-700">
//                   Multiple Tiers
//                 </span>
//               </div>
//               <div className="flex items-center gap-2 bg-white/80 backdrop-blur-xl px-4 py-2 rounded-full shadow-sm hover:shadow-md transition-all duration-200">
//                 <FiCheckCircle className="text-green-500 w-5 h-5" />
//                 <span className="text-sm font-medium text-gray-700">
//                   Premium Features
//                 </span>
//               </div>
//               <div className="flex items-center gap-2 bg-white/80 backdrop-blur-xl px-4 py-2 rounded-full shadow-sm hover:shadow-md transition-all duration-200">
//                 <FiCheckCircle className="text-green-500 w-5 h-5" />
//                 <span className="text-sm font-medium text-gray-700">
//                   Special Launch Offers
//                 </span>
//               </div>
//             </div>

//             {/* Button */}
//             <button className="bg-gradient-to-r from-purple-600 via-blue-600 to-pink-600 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-2xl hover:-translate-y-1 active:scale-95 transition-all duration-300 flex items-center gap-2 mx-auto">
//               <span>Notify Me When Available</span>
//               <LuSparkles className="w-5 h-5" />
//             </button>

//             {/* Note */}
//             <p className="text-sm text-gray-500 mt-6 tracking-wide">
//               Be the first to know when we launch! 🚀
//             </p>
//           </div>
//         </div>
//       </div>

//       <style>
//         {`
//   .animate-pulse-slow {
//     animation: pulse 3s ease-in-out infinite;
//   }
//   .animate-float {
//     animation: floatUpDown 3s ease-in-out infinite;
//   }
//   .animate-bounce-slow {
//     animation: bounce 1.8s infinite;
//   }

//   @keyframes floatUpDown {
//     0%, 100% { transform: translateY(0); }
//     50% { transform: translateY(-8px); }
//   }
// `}
//       </style>
//     </div>
//   );
// };

// export default SubscriptionTab;
